import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import AppText, { LinkText } from "../../components/Core/AppText/AppText";
import Button from "../../components/Core/Button/Button";
import ButtonNew from "../../components/Core/Button/ButtonNew";
import SplitButtonNew from "../../components/Core/Button/SplitButtonNew";
import DayPickerNew from "../../components/Core/DayPicker/DayPickerNew";
import Modal from "../../components/Core/Modal/Modal";
import TableLayout from "../../components/Layout/TableLayout";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import EmptyState from "../../components/Shared/EmptyState/EmptyState";
import InvoiceBatchProcessingModal from "../../components/Shared/InvoiceBatchProcessingModal/InvoiceBatchProcessingModal";
import BatchWarningSummary from "../../components/Shared/BatchWarningSummary/BatchWarningSummary";
import BatchStalenessBanner from "../../components/Shared/BatchStalenessBanner/BatchStalenessBanner";
import InvoiceViewer from "../../components/Shared/InvoiceViewer/InvoiceViewer";
import InvoiceBatchExportBanner from "../../components/Shared/InvoiceExportStatus/InvoiceBatchExportBanner";
import InvoiceExportStatus from "../../components/Shared/InvoiceExportStatus/InvoiceExportStatus";
import InvoiceDryRunMarker from "../../components/Shared/InvoiceDryRun/InvoiceDryRunMarker";
import InvoiceDryRunPanel from "../../components/Shared/InvoiceDryRun/InvoiceDryRunPanel";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import { SIDE_MENU_WIDTH } from "../../components/Shared/SideMenu/SideMenu";
import {
	InvoiceBatchCreateBreadcrumbs,
	InvoiceBatchDetailsBreadcrumbs,
} from "../../configs/breadcrumbNavigation";
import { routes } from "../../configs/constants/routes";
import useDebounce from "../../hooks/useDebounce";
import { ApiError, ApiErrorResponse } from "../../models/ApiResponse";
import Invoice from "../../models/Invoice";
import InvoiceBatch, {
	CreateInvoiceBatchRequest,
	InvoiceBatchFilter,
	InvoiceBatchScopeCustomerRow,
} from "../../models/InvoiceBatch";
import { useAllCustomers } from "../../networking/customer.hooks";
import { useOrganisation } from "../../networking/organisation.hooks";
import {
	UpdateInvoiceParams,
	useCreateInvoiceBatch,
	useDryRunInvoiceBatch,
	useFetchInvoiceBatch,
	useInvoiceBatchScopeCount,
	useInvoiceBatchStaleness,
	useProcessInvoiceBatch,
	useRefreshInvoiceBatch,
	useRollbackInvoiceBatch,
	useUpdateInvoiceInBatch,
} from "../../networking/invoice.hooks";
import CheckboxFilterGroup from "../../components/Shared/FilterGroup/CheckboxFilterGroup";
import { FlaskConical, RefreshCw, SlidersHorizontal } from "lucide-react";
import { useInternalUser } from "../../networking/internal-user.hooks";
import InvoiceBatchAdvancedFilterDialog from "./components/InvoiceBatchAdvancedFilterDialog";
import InvoiceBatchScopeSummary from "./components/InvoiceBatchScopeSummary";
import {
	countAdvancedFilterConstraints,
	pruneExcludedCustomerIds,
} from "./components/invoiceBatchFilter.utils";
import { useInvoiceBatchFilterOptions } from "./components/useInvoiceBatchFilterOptions";
import { wait } from "../../utils/promise.utils";
import { formatRoute } from "../../utils/route.utils";
import { formatCurrencyString } from "../../utils/string.utils";
import { createInvoiceBatchRowReadableError } from "../../utils/api.utils";
import { toast } from "../../utils/toast";
import {
	countOpenExportIssues,
	getInvoiceExportIssues,
	hasReleasedTimeReports,
	invoiceBatchRollbackErrorKey,
	invoiceCustomerName,
} from "../../utils/invoiceExport.utils";
import {
	dryRunInvoiceMarkers,
	dryRunStartErrorKey,
	invoiceRowElementId,
	isDryRunRunning,
	isDryRunSupported,
	shouldConfirmProcessAfterDryRun,
} from "../../utils/invoiceDryRun.utils";

/** How often a running dry run is re-read until it leaves RUNNING. */
const DRY_RUN_POLL_MS = 1500;

type Params = {
	id?: string;
};

const InvoiceBatchFormPage: React.FC = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { id } = useParams<Params>();
	const [searchParams] = useSearchParams();
	const isNew = !id;

	const { mutateAsync: fetchInvoiceBatch } = useFetchInvoiceBatch();

	// Every fetch of the batch keeps the customer expansion — the invoice
	// modal renders Kund/Besöksadress from the expanded customer, so a poll
	// or re-fetch that drops the expand would collapse invoice.customer back
	// to a bare { id } and blank those fields (seen after in-place refresh).
	const fetchExpandedInvoiceBatch = (invoiceBatchId: string) =>
		fetchInvoiceBatch({ invoiceBatchId, expand: ["invoices", "invoices.customer"] });
	const { mutateAsync: createInvoiceBatch } = useCreateInvoiceBatch();
	const { mutateAsync: processInvoiceBatch } = useProcessInvoiceBatch();
	const { mutateAsync: rollbackInvoiceBatch } = useRollbackInvoiceBatch();
	const { mutateAsync: refreshInvoiceBatch } = useRefreshInvoiceBatch();
	const { mutateAsync: updateInvoiceInBatch } = useUpdateInvoiceInBatch();
	const { mutateAsync: dryRunInvoiceBatch } = useDryRunInvoiceBatch();
	const [isLoadingBatch, setLoadingBatch] = useState(false);

	const [filter, setFilter] = useState<InvoiceBatchFilter>({});
	const [toDate, setToDate] = useState<Date | undefined>(new Date());
	const [invoiceDate, setInvoiceDate] = useState<Date | undefined>(new Date());
	const [invoiceBatch, setInvoiceBatch] = useState<InvoiceBatch>();
	const [selectedInvoice, setSelectedInvoice] = useState<Invoice>();
	const [isProcessing, setProcessing] = useState(false);
	const [isCreatingPreview, setCreatingPreview] = useState(false);
	const [showProcessingResult, setShowProcessingResult] = useState(false);
	const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
	const [processingInvoiceBatch, setProcessingInvoiceBatch] = useState<InvoiceBatch>();
	const [processingError, setProcessingError] = useState<ApiError>();
	const [isRefreshing, setRefreshing] = useState(false);
	const [isDryRunModalOpen, setDryRunModalOpen] = useState(false);
	const [isStartingDryRun, setStartingDryRun] = useState(false);
	const [dryRunPollRetry, setDryRunPollRetry] = useState(0);
	const [highlightedInvoiceId, setHighlightedInvoiceId] = useState<string>();

	const { data: organisation } = useOrganisation();

	// Visma.net invoicing is article based and manual rows carry no article
	// number, so they cannot be added/edited there (the API rejects them too).
	// Existing rows are still shown read-only.
	const canEditCustomInvoiceRows =
		organisation?.invoiceSettings?.exportType !== "VISMA_NET_BEMANNIA";

	// "Testkör" is offered for the invoice systems that support a dry run
	// (Visma.net and Fortnox), and only once the organisation is known
	const canDryRun = !!organisation && isDryRunSupported(organisation.invoiceSettings?.exportType);
	const dryRun = invoiceBatch?.dryRun;
	const isDryRunInProgress = isDryRunRunning(dryRun);

	// Markers on the invoice rows; empty while the result is outdated
	const dryRunMarkers = useMemo(() => dryRunInvoiceMarkers(dryRun), [dryRun]);

	const { user: dryRunStartedBy } = useInternalUser(dryRun?.startedBy);
	const dryRunStartedByName = dryRunStartedBy
		? [dryRunStartedBy.firstName, dryRunStartedBy.lastName].filter(Boolean).join(" ")
		: undefined;

	const filterOptions = useInvoiceBatchFilterOptions();
	const advancedFilterCount = countAdvancedFilterConstraints(filter);

	// Memoised before debouncing: useDebounce depends on the value's identity,
	// so a fresh object every render would re-arm the timer on the very
	// re-render its own setState causes, and never settle.
	const pendingScopeRequest = useMemo(
		() =>
			isNew && toDate ? { to: dayjs(toDate).endOf("day").toISOString(), ...filter } : undefined,
		[isNew, toDate, filter]
	);

	// Debounced so toggling several filters in a row issues one count, not one
	// per click. Only projected for a new batch — an existing one is read-only.
	const scopeRequest = useDebounce(pendingScopeRequest, 300);

	const { scopeCount, isFetching: isCountingScope } = useInvoiceBatchScopeCount(scopeRequest);

	// Only a PREVIEW draft can meaningfully drift; other statuses always
	// answer "not stale" anyway
	const { staleness } = useInvoiceBatchStaleness(id, invoiceBatch?.status === "PREVIEW");

	const hasNothingToInvoice = scopeCount?.counts?.invoiceableTimeReports === 0; // Drives both the footer count and whether the batch can be processed at all
	const includedInvoiceCount =
		invoiceBatch?.invoices.filter((invoice) => invoice.include).length ?? 0;

	// Per-invoice export outcome (Visma.net): counted from the invoices rather
	// than `exportSummary`, so an action's result shows as soon as the batch is
	// refetched
	const exportFollowUp = useMemo(() => {
		const invoices = invoiceBatch?.invoices.map(({ invoice }) => invoice) ?? [];

		return {
			counts: countOpenExportIssues(invoices),
			hasReleasedTimeReports: hasReleasedTimeReports(invoices),
			hasAnyIssue: invoices.some((invoice) => getInvoiceExportIssues(invoice).length > 0),
		};
	}, [invoiceBatch]);

	// The count endpoint cannot reproduce a processed batch's scope (its time
	// reports are already invoiced), so the read-only dialog renders from what
	// the batch persisted: included customers from its own invoices, excluded
	// ids resolved to names via the cached full customer list — fetched only
	// once the dialog is opened for a batch that actually has exclusions.
	const hasManualExclusions = !!filter.excludedCustomerIds?.length;

	const { customers: allCustomers } = useAllCustomers(
		undefined,
		!isNew && showAdvancedFilter && hasManualExclusions
	);

	const readOnlyScopeRows = useMemo<InvoiceBatchScopeCustomerRow[] | undefined>(() => {
		if (isNew) return undefined;

		const nameById = new Map(allCustomers.map((customer) => [customer.id, customer.name]));

		// The customer's actual name, not `invoiceName` — the billing name is
		// often a parent entity ("Stockholms stad") shared by many customers.
		// The batch is loaded with expand invoices.customer, so the name is
		// usually right on the invoice; the cached list and billing name are
		// fallbacks. Deduped by customer id: a template can split one customer
		// into several invoices, but the selection list is about customers.
		const includedById = new Map<string, InvoiceBatchScopeCustomerRow>();

		for (const { invoice } of invoiceBatch?.invoices ?? []) {
			const customer = invoice.customer as { id: string; name?: string };

			if (!includedById.has(customer.id))
				includedById.set(customer.id, {
					id: customer.id,
					name: customer.name ?? nameById.get(customer.id) ?? invoice.invoiceName,
					invoiceableTimeReports: 0,
				});
		}

		const excluded = (filter.excludedCustomerIds ?? []).map((id) => ({
			id,
			name: nameById.get(id) ?? id,
			invoiceableTimeReports: 0,
			excluded: true,
		}));

		return [...includedById.values(), ...excluded].sort((a, b) =>
			(a.name ?? "").localeCompare(b.name ?? "", "sv")
		);
	}, [isNew, invoiceBatch, allCustomers, filter]);

	useEffect(() => {
		async function load() {
			if (id) {
				setLoadingBatch(true);
				const data = await fetchExpandedInvoiceBatch(id).catch(() => undefined);
				setLoadingBatch(false);

				if (data) {
					setInvoiceBatch(data);
					setToDate(new Date(data.to));
					setInvoiceDate(new Date(data.invoiceDate));
					// Batches created before customer filtering only carry the
					// deprecated single `pool`, so fall back to it
					setFilter(data.filter ?? (data.pool ? { pools: [data.pool] } : {}));
				} else {
					toast.error("invoiceBatch.errors.failedToRetrieve");
				}
			} else {
				// "Ny körning med samma urval" — seed the customer selection from
				// the source batch; dates stay fresh, a redo targets a new period.
				// Stale exclusions are inert and pruned at submit as usual.
				const fromId = searchParams.get("from");

				if (fromId) {
					const source = await fetchInvoiceBatch({ invoiceBatchId: fromId }).catch(() => undefined);

					if (source) setFilter(source.filter ?? (source.pool ? { pools: [source.pool] } : {}));
				}
			}
		}

		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	// A running dry run (started here, or by someone else before the page was
	// opened) is re-read until it leaves RUNNING; the panel and the dry-run
	// modal both render from the batch. Every fetch replaces the batch, which
	// re-arms this effect for the next round.
	useEffect(() => {
		if (!invoiceBatch || !isDryRunRunning(invoiceBatch.dryRun)) return;

		let cancelled = false;

		const timer = setTimeout(async () => {
			const data = await fetchExpandedInvoiceBatch(invoiceBatch.id).catch(() => undefined);

			if (cancelled) return;
			if (data) setInvoiceBatch(data);
			else setDryRunPollRetry((retry) => retry + 1);
		}, DRY_RUN_POLL_MS);

		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [invoiceBatch, dryRunPollRetry]);

	async function handleSubmit() {
		// Rollback previous invoice batch before creating new preview
		if (invoiceBatch) await rollback();

		if (!toDate) return;

		// Exclusions referencing customers outside the resolved scope are inert,
		// so prune them and persist only ids that actually did something
		const request: CreateInvoiceBatchRequest = {
			to: dayjs(toDate).endOf("day").toISOString(),
			invoiceDate: invoiceDate!.toISOString(),
			...pruneExcludedCustomerIds(filter, scopeCount?.customers),
		};

		setCreatingPreview(true);

		try {
			const created = await createInvoiceBatch(request);
			await pollUntilFinishedCreatingPreview(created.id);
			navigate(formatRoute(routes.INVOICE_BATCH_DETAILS, [created.id]));
			setCreatingPreview(false);
		} catch (e) {
			const errMsg = t(("invoiceBatch.error." + (e as ApiErrorResponse)?.error?.code) as any);
			toast.error(errMsg);
		}
	}

	/**
	 * A draft or failed batch is thrown away entirely; a processed one is only
	 * reverted, so it keeps its row in the list. The three cases only differ in
	 * those two flags, so they share one request and one set of toasts.
	 */
	const rollbackVariant = (status: InvoiceBatch["status"]) => {
		switch (status) {
			case "PREVIEW":
				return {
					confirm: "invoiceBatch.confirmRollbackAndRemove",
					deleteAfterRollback: true,
					success: "invoiceBatch.rollbackAndRemoveSuccess",
				};
			case "PROCESSED":
				return {
					confirm: "invoiceBatch.confirmRollback",
					deleteAfterRollback: false,
					success: "invoiceBatch.rollbackSuccess",
				};
			case "FAILED":
				// Nothing usable to lose, so no confirmation
				return { deleteAfterRollback: true, success: "invoiceBatch.rollbackSuccess" };
			default:
				return undefined;
		}
	};

	const rollback = async () => {
		if (!invoiceBatch) return false;

		const variant = rollbackVariant(invoiceBatch.status);

		if (!variant) return false;
		if (variant.confirm && !window.confirm(t(variant.confirm as any))) return false;

		const toastId = toast.loading(t("invoiceBatch.rollingBack"));

		try {
			await rollbackInvoiceBatch({
				invoiceBatchId: invoiceBatch.id,
				deleteAfterRollback: variant.deleteAfterRollback,
			});

			toast.success(t(variant.success as any), { id: toastId });
			return true;
		} catch (e) {
			// Replaces the spinner rather than stacking under it — a loading
			// toast is never auto-dismissed, so the error path must resolve it
			toast.error(t(invoiceBatchRollbackErrorKey(e)), { id: toastId });
			return false;
		}
	};

	/**
	 * Rebuilds the draft's preview in place (same batch id) after the
	 * underlying data changed. The recompute starts from scratch, so manual
	 * draft edits are confirmed away first.
	 */
	async function handleRefresh() {
		if (!invoiceBatch) return;

		const hasManualEdits = invoiceBatch.invoices.some(
			({ include, modified, invoice }) =>
				!include || modified || !!invoice.customInvoiceRows?.length
		);

		if (hasManualEdits && !window.confirm(t("batchRefresh.confirmDiscardEdits"))) return;

		setRefreshing(true);

		try {
			const initial = await refreshInvoiceBatch(invoiceBatch.id);
			setInvoiceBatch(initial);

			const rebuilt = await pollUntilFinishedCreatingPreview(initial.id);
			setInvoiceBatch(rebuilt);

			if (rebuilt.status === "FAILED") toast.error(t("batchRefresh.failed"));
			else toast.success(t("batchRefresh.success"));
		} catch (e) {
			toast.error(t("batchRefresh.failed"));
		} finally {
			setRefreshing(false);
		}
	}

	async function handleCancel() {
		if (await rollback()) {
			navigate(routes.INVOICE_BATCHES);
		}
	}

	/**
	 * Opens modal with processing result for an already
	 * processed invoice batch
	 */
	function handleShowProcessingResult() {
		setShowProcessingResult(true);
		setProcessingInvoiceBatch(invoiceBatch);
	}

	/**
	 * Starts a dry run ("Testkör"): the invoice system is read and checked but
	 * nothing is written, and the batch stays PREVIEW. Progress shows in the
	 * processing modal; the result stays on the page as a panel.
	 */
	async function handleDryRun() {
		if (!invoiceBatch) return;

		setProcessingError(undefined);
		setStartingDryRun(true);

		try {
			const started = await dryRunInvoiceBatch(invoiceBatch.id);

			// The response may come without the expanded invoices, so only the
			// dry-run state is taken from it; polling picks up the rest
			setInvoiceBatch((current) => (current ? { ...current, dryRun: started.dryRun } : current));
			setDryRunModalOpen(true);

			// No dry-run state in the answer: read the batch so polling can start
			if (!started.dryRun) await reloadInvoiceBatch();
		} catch (e) {
			toast.error(t(dryRunStartErrorKey(e)));
			// Someone else's run (409) is then shown and followed like our own
			await reloadInvoiceBatch();
		} finally {
			setStartingDryRun(false);
		}
	}

	function handleCloseDryRunModal() {
		setDryRunModalOpen(false);
		setProcessingError(undefined);
	}

	/** From a finding in the dry-run panel to the invoice's row in the table. */
	function handleShowInvoiceRow(invoiceId: string) {
		document
			.getElementById(invoiceRowElementId(invoiceId))
			?.scrollIntoView({ behavior: "smooth", block: "center" });

		setHighlightedInvoiceId(invoiceId);
		setTimeout(
			() => setHighlightedInvoiceId((current) => (current === invoiceId ? undefined : current)),
			2500
		);
	}

	async function handleProcess() {
		if (invoiceBatch) {
			// Never blocked, but a current dry run that found errors is not
			// ignored silently
			if (
				shouldConfirmProcessAfterDryRun(invoiceBatch.dryRun) &&
				!window.confirm(t("invoiceBatch.dryRun.confirmProcess"))
			)
				return;

			setProcessing(true);
			try {
				const data = await processInvoiceBatch(invoiceBatch.id);
				setProcessingInvoiceBatch(data);
				setProcessingInvoiceBatch(await pollUntilFinishedProcessing());
			} catch (e) {
				setProcessingError((e as ApiErrorResponse)?.error);
			}
		}
	}

	async function pollUntilFinishedProcessing(): Promise<InvoiceBatch> {
		await wait();

		const data = await fetchExpandedInvoiceBatch(invoiceBatch!.id).catch((error) => {
			// eslint-disable-next-line no-console
			console.log("Failed getting invoice batch, will retry", error);
			return undefined;
		});

		if (!data) {
			return await pollUntilFinishedProcessing();
		} else if (data.status === "PROCESSING") {
			setProcessingInvoiceBatch(data);
			return await pollUntilFinishedProcessing();
		} else {
			return data;
		}
	}

	async function pollUntilFinishedCreatingPreview(
		newInvoiceBatchId: string
	): Promise<InvoiceBatch> {
		await wait();

		const data = await fetchExpandedInvoiceBatch(newInvoiceBatchId).catch((error) => {
			// eslint-disable-next-line no-console
			console.log("Failed getting invoice batch, will retry", error);
			return undefined;
		});

		if (!data) {
			return await pollUntilFinishedProcessing();
		} else if (data.status === "INITIAL") {
			setInvoiceBatch(data);
			return await pollUntilFinishedCreatingPreview(newInvoiceBatchId);
		} else {
			return data;
		}
	}

	async function handleToggleInvoice(invoiceId: string) {
		if (invoiceBatch) {
			const invoice = invoiceBatch.invoices.find((invoice) => invoice.invoice.id === invoiceId);

			if (invoice) {
				invoice.include = !invoice.include;
			}

			await updateInvoiceInBatch({
				invoiceBatchId: invoiceBatch.id,
				invoiceId,
				change: { include: invoice?.include },
			}).catch(() => undefined);

			const data = await fetchExpandedInvoiceBatch(invoiceBatch.id).catch(() => undefined);

			if (data) {
				setInvoiceBatch(data);
			}
		}
	}

	async function handleInvoiceSubmit(customInvoiceRows: UpdateInvoiceParams["customInvoiceRows"]) {
		if (selectedInvoice && invoiceBatch) {
			await updateInvoiceInBatch({
				invoiceBatchId: invoiceBatch.id,
				invoiceId: selectedInvoice.id,
				change: { customInvoiceRows },
			}).catch((e: ApiErrorResponse) => {
				toast.apiError(createInvoiceBatchRowReadableError(e?.error?.detail ?? ""));
			});

			const updatedInvoiceBatch = await fetchExpandedInvoiceBatch(invoiceBatch.id).catch(
				() => undefined
			);

			if (updatedInvoiceBatch) {
				const invoice = updatedInvoiceBatch.invoices.find(
					(invoice) => invoice.invoice.id === selectedInvoice.id
				);
				setInvoiceBatch(updatedInvoiceBatch);
				setSelectedInvoice(invoice?.invoice);
			}
		}
	}

	/** Refetch after a per-invoice export follow-up action. */
	async function reloadInvoiceBatch() {
		if (!invoiceBatch) return;

		const data = await fetchExpandedInvoiceBatch(invoiceBatch.id).catch(() => undefined);

		if (data) setInvoiceBatch(data);
	}

	/**
	 * From the processing result straight to the processed batch, so the
	 * per-invoice follow-ups can be handled on this page.
	 */
	function handleShowProcessedBatch() {
		if (processingInvoiceBatch) setInvoiceBatch(processingInvoiceBatch);

		setProcessing(false);
		setShowProcessingResult(false);
		setProcessingError(undefined);
	}

	function handleCloseProcessingModal() {
		if (showProcessingResult) {
			setShowProcessingResult(false);
		} else {
			navigate(routes.INVOICE_BATCHES);
		}
	}

	return (
		<>
			<Breadcrumbs items={isNew ? InvoiceBatchCreateBreadcrumbs : InvoiceBatchDetailsBreadcrumbs} />
			<div className="p-4 px-6">
				<StyledInvoiceBatch>
					<div className="form">
						{/* Dates first: they define the run, the filters only narrow it */}
						<div className="mb-6 flex flex-col gap-4">
							<DayPickerNew
								value={toDate}
								onChange={setToDate}
								label="invoiceBatch.to"
								description="invoiceBatch.toDescription"
								disabled={!isNew}
							/>

							<DayPickerNew
								value={invoiceDate}
								onChange={setInvoiceDate}
								label="invoice.invoiceDate"
								description="invoiceBatch.invoiceDateDescription"
								disabled={!isNew}
							/>
						</div>

						{/* Dividers fence the filter block off from the dates above and
						    the submit action below */}
						<hr className="mb-5 border-gray-200" />

						{/* Pool and subsidiary stay primary on the form; everything else
						    scopes in the advanced dialog. An empty selection means "Alla",
						    and the subsidiary group hides itself for orgs without any. */}
						<CheckboxFilterGroup
							label={t("POOL" as any)}
							options={filterOptions.pools}
							selected={filter.pools ?? []}
							disabled={!isNew}
							// An emptied group is dropped rather than left as [], so an
							// unconstrained filter serialises as {}
							onChange={(pools) =>
								setFilter((current) => {
									const next = { ...current };
									if (pools.length) next.pools = pools;
									else delete next.pools;
									return next;
								})
							}
							allLabel={t("invoiceBatch.filter.allPools")}
						/>

						<CheckboxFilterGroup
							label={t("SUBSIDIARY")}
							options={filterOptions.subsidiaries}
							selected={filter.subsidiaryIds ?? []}
							disabled={!isNew}
							onChange={(subsidiaryIds) =>
								setFilter((current) => {
									const next = { ...current };
									if (subsidiaryIds.length) next.subsidiaryIds = subsidiaryIds;
									else delete next.subsidiaryIds;
									return next;
								})
							}
							allLabel={t("batchFilter.allSubsidiaries")}
						/>

						<button
							type="button"
							onClick={() => setShowAdvancedFilter(true)}
							className="mb-5 flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
						>
							<SlidersHorizontal size={14} />
							{t("invoiceBatch.filter.advancedSelection")}
							{advancedFilterCount > 0 && (
								<span className="text-gray-500">({advancedFilterCount})</span>
							)}
						</button>

						{!isNew && hasManualExclusions && (
							<div className="mb-5 text-sm text-gray-600">
								{t("invoiceBatch.filter.excludedCustomersCount", {
									count: filter.excludedCustomerIds!.length,
								})}
							</div>
						)}

						<hr className="mb-5 border-gray-200" />

						{isNew && (
							<>
								<InvoiceBatchScopeSummary scopeCount={scopeCount} isLoading={isCountingScope} />
								<Button
									onClick={handleSubmit}
									margin="0 0 2.5rem 0"
									width={"230px"}
									disabled={hasNothingToInvoice || isCreatingPreview}
								>
									{t("invoiceBatch.submit")}
								</Button>{" "}
							</>
						)}

						<InvoiceBatchAdvancedFilterDialog
							open={showAdvancedFilter}
							onOpenChange={setShowAdvancedFilter}
							filter={filter}
							onChange={setFilter}
							options={filterOptions}
							customers={isNew ? scopeCount?.customers : readOnlyScopeRows}
							isLoading={isNew && isCountingScope}
							readOnly={!isNew}
						/>

						{invoiceBatch?.status !== "PROCESSING" && !!invoiceBatch?.processingResult && (
							<LinkText size="s" onClick={handleShowProcessingResult}>
								{t("invoiceBatch.showProcessingResult")}
							</LinkText>
						)}
					</div>
					<div className="content">
						{(isLoadingBatch || isCreatingPreview || isRefreshing) && <LoadingIndicator />}
						{!isLoadingBatch && !isCreatingPreview && !invoiceBatch && (
							<EmptyState label="invoiceBatch.emptyState.notSubmitted" />
						)}
						{!isLoadingBatch && !isRefreshing && invoiceBatch && (
							<>
								<BatchStalenessBanner
									staleness={staleness}
									onRefresh={handleRefresh}
									isRefreshing={isRefreshing}
								/>

								<BatchWarningSummary
									warnings={invoiceBatch.warnings}
									errors={invoiceBatch.errors}
									issueNamespace="invoiceBatch"
									period={{ end: invoiceBatch.to }}
								/>

								{invoiceBatch.status === "PREVIEW" && dryRun && (
									<InvoiceDryRunPanel
										dryRun={dryRun}
										invoices={invoiceBatch.invoices}
										startedByName={dryRunStartedByName}
										onShowInvoice={handleShowInvoiceRow}
									/>
								)}

								{invoiceBatch.status === "PROCESSED" && (
									<InvoiceBatchExportBanner
										counts={exportFollowUp.counts}
										hasReleasedTimeReports={exportFollowUp.hasReleasedTimeReports}
										invoiceBatchId={invoiceBatch.id}
									/>
								)}

								<TableLayout>
									<table>
										<thead>
											<tr>
												<th></th>
												{/* <th>{t("invoice.invoiceNumber")}</th> */}
												<th style={{ minWidth: 300 }}>{t("invoice.customer")}</th>
												<th style={{ width: 200 }}>{t("invoice.totalExclVat")}</th>
												<th style={{ width: 200 }}>{t("invoice.totalInclVat")}</th>
												{exportFollowUp.hasAnyIssue && (
													<th style={{ minWidth: 260 }}>{t("invoiceBatch.exportStatus.column")}</th>
												)}
											</tr>
										</thead>
										<tbody>
											{invoiceBatch.invoices.map(({ invoice, include }, index) => {
												const marker = dryRunMarkers.get(invoice.id);

												return (
													<tr
														key={index}
														id={invoiceRowElementId(invoice.id)}
														className={
															highlightedInvoiceId === invoice.id
																? "outline outline-2 -outline-offset-2 outline-blue-500"
																: undefined
														}
													>
														<td>
															{invoiceBatch.status === "PREVIEW" && (
																<input
																	onChange={() => handleToggleInvoice(invoice.id)}
																	checked={include}
																	type="checkbox"
																/>
															)}
														</td>

														{/* <td onClick={() => setSelectedInvoice(invoice)}>
																	{invoice.invoiceNumber}
																</td> */}
														<td onClick={() => setSelectedInvoice(invoice)}>
															{marker && <InvoiceDryRunMarker severity={marker} />}
															{invoice.invoiceName} ({invoice.customerNumber})
														</td>
														<td onClick={() => setSelectedInvoice(invoice)}>
															{formatCurrencyString(invoice.total - invoice.totalVAT)}
														</td>
														<td onClick={() => setSelectedInvoice(invoice)}>
															{formatCurrencyString(invoice.total)}
														</td>
														{exportFollowUp.hasAnyIssue && (
															<td className="cursor-default align-top">
																<InvoiceExportStatus
																	invoice={invoice}
																	customerName={invoiceCustomerName(invoice)}
																	onChanged={reloadInvoiceBatch}
																/>
															</td>
														)}
													</tr>
												);
											})}
										</tbody>
									</table>
								</TableLayout>
								<div className="total">
									<div>
										<AppText weight="medium" size="l">
											{formatCurrencyString(invoiceBatch.total)} (
											{t("invoice.journal.inclVAT").toLowerCase()})
										</AppText>
										<AppText>
											{t("invoiceBatch.numInvoices")}: {includedInvoiceCount}
										</AppText>
									</div>
									{/* The journal action renders as an anchor itself rather than a
									    button wrapped in one, so all three are the same kind of flex
									    item and line up */}
									<div className="flex items-center gap-3">
										{(invoiceBatch.status === "PREVIEW" || invoiceBatch.status === "FAILED") && (
											<ButtonNew variant="neutral" onClick={handleRefresh} disabled={isRefreshing}>
												<RefreshCw
													size={16}
													className={isRefreshing ? "animate-spin" : undefined}
												/>
												{t("batchRefresh.button")}
											</ButtonNew>
										)}

										{(invoiceBatch.status === "PREVIEW" || invoiceBatch.status === "FAILED") && (
											<ButtonNew
												variant="danger"
												onClick={handleCancel}
												// Rollback is refused (409) while a dry run is running
												disabled={isDryRunInProgress}
											>
												{t("invoiceBatch.cancel")}
											</ButtonNew>
										)}

										<ButtonNew
											variant="dark"
											href={formatRoute(routes.INVOICE_JOURNAL, [invoiceBatch.id])}
											target="_blank"
											rel="noopener noreferrer"
										>
											{t("invoiceBatch.showInvoiceJournal")}
										</ButtonNew>

										{invoiceBatch.status === "PREVIEW" && (
											<SplitButtonNew
												variant="primary"
												onClick={handleProcess}
												// Unchecking every customer leaves nothing to create; the
												// real run is refused (409) while a dry run is running
												disabled={!includedInvoiceCount || isDryRunInProgress}
												menuLabel={t("invoiceBatch.moreActions")}
												// The dry run ("Testkör") sits behind the chevron, next
												// to the real run it rehearses
												items={
													canDryRun
														? [
																{
																	label: t("invoiceBatch.dryRun.button"),
																	icon: <FlaskConical />,
																	title: t("invoiceBatch.dryRun.buttonHint"),
																	onSelect: handleDryRun,
																	disabled:
																		!includedInvoiceCount || isDryRunInProgress || isStartingDryRun,
																},
														  ]
														: []
												}
											>
												{t("invoiceBatch.process")}
											</SplitButtonNew>
										)}
									</div>
								</div>

								{selectedInvoice && (
									<Modal
										maxWidth="920px"
										padding="4rem 2rem"
										centerContent={false}
										onClose={() => setSelectedInvoice(undefined)}
									>
										<InvoiceViewer
											invoice={selectedInvoice}
											editable={canEditCustomInvoiceRows}
											onSubmit={handleInvoiceSubmit}
										/>
										<Button
											margin="1.5rem 0 0 0"
											backgroundColor="secondary"
											onClick={() => setSelectedInvoice(undefined)}
										>
											{t("CLOSE")}
										</Button>
									</Modal>
								)}

								{isDryRunModalOpen && (
									<InvoiceBatchProcessingModal
										dryRun
										invoiceBatch={invoiceBatch}
										error={processingError}
										onClose={handleCloseDryRunModal}
									/>
								)}

								{(isProcessing || showProcessingResult) && (
									<InvoiceBatchProcessingModal
										invoiceBatch={processingInvoiceBatch}
										error={processingError}
										onClose={handleCloseProcessingModal}
										onShowInvoiceBatch={handleShowProcessedBatch}
									/>
								)}
							</>
						)}
					</div>
				</StyledInvoiceBatch>
			</div>
		</>
	);
};

export default InvoiceBatchFormPage;

const leftColWidth = "275px";

const StyledInvoiceBatch = styled.div`
	display: flex;

	.form {
		width: ${leftColWidth};
		min-height: 80vh;
		padding-right: 1rem;
		border-right: 2px solid ${(props) => props.theme.colors.COLOR_DIVIDER_GRAY};
	}

	.content {
		margin-left: 2rem;
		flex: 1;
		position: relative;
		padding-bottom: 5rem;
	}

	.total {
		position: fixed;
		bottom: 0;
		background-color: ${(props) => props.theme.colors.COLOR_WHITE};
		width: calc(100% - ${leftColWidth} - ${SIDE_MENU_WIDTH}px - 4.5rem);
		left: ${leftColWidth} + ${SIDE_MENU_WIDTH}px;
		border-radius: 10px 10px 0 0;
		padding: 1.25rem 1.5rem;
		box-shadow: 0px -1px 10px rgba(0, 0, 0, 0.1);

		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	.content,
	.total {
		max-width: 1400px;
	}
`;
